import { useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  Listing,
  MarketListing,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { sleep } from '@backmarket/utils/async/sleep'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import {
  BUYBOX_STATE,
  LISTING_PUBLICATION_STATE_COMPUTING_INTERVAL,
} from '~/scopes/listings/Listings.constants'

const listings = ref<Listing[]>([])
const listingsCount = ref(0)
const isFetchingListings = ref(false)

export function useListings() {
  const { FF_BO_SELLER_ENABLE_GET_BACKBOX_BASED_ON_IS_PENDING } =
    useRuntimeConfig().public
  const { isFeatureEnabled } = useBusinessFeaturesStore()

  async function getOldBackboxInfo(listingId: string) {
    const { data } = await useHttpFetch(merchantsAPI.getOldBackboxInfo, {
      pathParams: {
        listingId,
      },
    })

    return data.value
  }

  async function getListings(
    queryParams: Record<string, string | number | string[]>,
  ) {
    isFetchingListings.value = true

    const { data } = await useHttpFetch(merchantsAPI.getListings, {
      queryParams,
    })

    listings.value = data.value?.results || []
    listingsCount.value = data.value?.count || 0
    isFetchingListings.value = false
  }

  async function getListing(listingId: string) {
    const { data } = await useHttpFetch(merchantsAPI.getListing, {
      pathParams: {
        listingId,
      },
    })

    return data.value
  }

  function refreshListing(updatedListing: Listing | null) {
    if (listings.value.length && updatedListing) {
      const listingIndex = listings.value.findIndex(
        (listing) => listing.id === updatedListing.id,
      )
      listings.value[listingIndex] = updatedListing
    }
  }

  async function setWatcherOnListingUpdateStatus(
    listing: Listing | null,
    retries = 5,
    isForGetBackbox = false,
  ) {
    if (!listing) return

    if (!FF_BO_SELLER_ENABLE_GET_BACKBOX_BASED_ON_IS_PENDING) {
      let shouldRetry = false

      if (isForGetBackbox) {
        const oldBackboxInfo = await getOldBackboxInfo(
          listing.legacyId.toString(),
        )

        shouldRetry =
          oldBackboxInfo?.markets.some(
            (market) => market.buyboxState === BUYBOX_STATE.PENDING,
          ) || false
      } else {
        const updatedListing = await getListing(listing.id)

        shouldRetry = updatedListing?.isPublicationStateComputing || false
      }

      await sleep(LISTING_PUBLICATION_STATE_COMPUTING_INTERVAL)

      if (shouldRetry && retries > 0) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        setWatcherOnListingUpdateStatus(listing, retries - 1, isForGetBackbox)
      } else {
        refreshListing(await getListing(listing.id))
      }
    } else {
      const hasPublicationStateComputing = listing.isPublicationStateComputing

      const hasOneMarketWithPendingBackbox =
        Object.keys(listing.markets.offline).some(
          (market) =>
            listing.markets.offline[market as MarketCountryCode]
              .isBackboxPending,
        ) ||
        Object.keys(listing.markets.withoutBackbox).some(
          (market) =>
            listing.markets.withoutBackbox[market as MarketCountryCode]
              .isBackboxPending,
        )

      if (hasOneMarketWithPendingBackbox || hasPublicationStateComputing) {
        await sleep(LISTING_PUBLICATION_STATE_COMPUTING_INTERVAL)
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        setWatcherOnListingUpdateStatus(await getListing(listing.id))
      } else {
        refreshListing(listing)
      }
    }
  }

  async function getBackboxForMarket(
    listingId: string,
    market: MarketCountryCode,
  ) {
    const { data } = await useHttpFetch(merchantsAPI.getBackboxForMarket, {
      pathParams: {
        listingId,
      },
      body: {
        market,
      },
    })

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setWatcherOnListingUpdateStatus(data.value as Listing, 5, true)
  }

  async function getAllBackboxesForListing(listingId: string) {
    const { data } = await useHttpFetch(
      merchantsAPI.getAllBackboxesForListing,
      {
        pathParams: {
          listingId,
        },
      },
    )

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setWatcherOnListingUpdateStatus(data.value as Listing, 5, true)
  }

  async function updateListing(
    listingId: string,
    body: Record<string, unknown>,
  ) {
    const { data } = await useHttpFetch(merchantsAPI.updateListing, {
      pathParams: {
        listingId,
      },
      body,
    })

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setWatcherOnListingUpdateStatus(data.value as Listing)
  }

  async function publish(listingId: string) {
    await useHttpFetch(merchantsAPI.publishListing, {
      pathParams: {
        listingId,
      },
    })
  }

  async function archive(listingId: string) {
    await useHttpFetch(merchantsAPI.archiveListing, {
      pathParams: {
        listingId,
      },
    })
  }

  /**
   * Determines whether to show the Sales Maximizer price block instead of the alert.
   * Displays the block when:
   * - minimum price (or price if backpricer is not enabled) is above sales maximizer price
   * - and the sales maximizer price is below price to win backbox
   * @param {MarketListing} marketItem - The market listing item to evaluate
   * @returns {boolean} Whether to show the sales maximizer block
   */
  function shouldShowPricingStrategy(marketItem: MarketListing) {
    if (
      !isFeatureEnabled('isSalesMaximizerPriceEnabled') ||
      marketItem.pricingStrategy === null
    ) {
      return false
    }

    const price = marketItem.isBackpricerEnabledForListing
      ? marketItem.minimumPrice?.amount
      : marketItem.price?.amount

    if (!price || !marketItem.pricingStrategy?.salesMaximizerPrice?.amount) {
      return false
    }

    const minimumPriceIsAboveSalesMaximizerPrice =
      price > marketItem.pricingStrategy.salesMaximizerPrice.amount

    const salesMaximizerPriceIsLessThenPriceToWinBackbox =
      marketItem.pricingStrategy.salesMaximizerPrice.amount <
      marketItem.backboxPrice?.amount

    return (
      minimumPriceIsAboveSalesMaximizerPrice &&
      salesMaximizerPriceIsLessThenPriceToWinBackbox
    )
  }

  async function setMaximizerPrice(
    listingId: string,
    market: MarketCountryCode,
  ) {
    const { data } = await useHttpFetch(merchantsAPI.setSalesMaximizerPrice, {
      pathParams: {
        listingId,
      },
      body: {
        market,
      },
    })

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setWatcherOnListingUpdateStatus(data.value as Listing, 5, true)
  }

  function formatListingToTrackingEvent(
    listing: Listing,
    forMarket: (MarketListing & { market: MarketCountryCode }) | null = null,
  ) {
    const onlineMarkets = [
      ...Object.keys(listing.markets.withBackbox).map((market) => ({
        market,
        ...listing.markets.withBackbox[market as MarketCountryCode],
      })),
      ...Object.keys(listing.markets.withoutBackbox).map((market) => ({
        market,
        ...listing.markets.withoutBackbox[market as MarketCountryCode],
      })),
    ] as (MarketListing & { market: MarketCountryCode })[]

    const markets = forMarket === null ? onlineMarkets : [forMarket]

    return {
      listing_id: listing.id,
      listing_title: listing.title,
      product_public_id: listing.productId,
      listing_backbox_grade: listing.aestheticGrade.value,
      listing_special_offer_type: listing.specialOfferType.value,
      listing_markets: markets.map((listingMarket) => {
        const isOptimalPriceDisplayed = shouldShowPricingStrategy(listingMarket)

        return {
          market: listingMarket.market,
          currency: listingMarket.price?.currency ?? '',
          has_backbox: listingMarket.hasBackbox,
          is_optimal_price_displayed: isOptimalPriceDisplayed,
          minimum_price: listingMarket.minimumPrice?.amount ?? '',
          target_price: listingMarket.price?.amount ?? '',
          ...(listingMarket.hasBackbox && {
            selling_price: listingMarket.backboxPrice?.amount ?? '',
            backbox_grade: listingMarket.backboxGrade.value,
          }),
          ...(!listingMarket.hasBackbox && {
            selling_price: listingMarket.currentBackboxPrice?.amount ?? '',
            price_to_win_backbox: listingMarket.backboxPrice?.amount ?? '',
          }),
          ...(isOptimalPriceDisplayed && {
            optimal_price:
              listingMarket.pricingStrategy?.salesMaximizerPrice?.amount ?? '',
            range_estimaded_sales:
              listingMarket.pricingStrategy?.estimatedDemand ?? '',
          }),
        }
      }),
    }
  }

  return {
    isFetchingListings: computed(() => isFetchingListings.value),
    listingsCount: computed(() => listingsCount.value),
    listings: computed(() => listings.value),
    archive,
    getAllBackboxesForListing,
    getBackboxForMarket,
    getListing,
    getListings,
    publish,
    updateListing,
    shouldShowPricingStrategy,
    setMaximizerPrice,
    formatListingToTrackingEvent,
  }
}
